// src/pages/Contact.tsx
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Button from "../shared/button/Button";
import { trackLead } from "../analytics/track";

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
    inquiryType: "general",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_ACCESS_KEY,
          subject: "New Resources Contact Submission — Babblebots Website",
          ...formData,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || "Failed to submit form");
      }

      setSubmitted(true);
      trackLead("resources_contact", "Resources Contact");
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Failed to submit the form. Please try again");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Babblebots - Get in Touch</title>
        <meta
          name="description"
          content="Contact Babblebots for sales inquiries, support, or partnership opportunities. Our team is ready to help transform your recruitment process."
        />
      </Helmet>

      <div className="min-h-screen bg-gray-950 text-gray-50">
        {/* Hero Section */}
        <section className="relative overflow-hidden border-b border-gray-800 bg-gradient-to-b from-gray-900 to-gray-950 px-6 py-20 lg:py-32">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-cyan-900/20 via-gray-950 to-gray-950"></div>

          <div className="relative mx-auto max-w-7xl">
            <div className="mx-auto max-w-3xl text-center">
              <h1 className="font-satoshi mb-6 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
                Let's Talk About Your{" "}
                <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                  Hiring Goals
                </span>
              </h1>
              <p className="font-manrope text-lg text-gray-300 sm:text-xl">
                Whether you're looking to scale your recruitment, reduce
                time-to-hire, or eliminate bias, our team is here to help. Get
                in touch and let's build the future of hiring together.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Content */}
        <section className="px-6 py-16 lg:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
              {/* Contact Form */}
              <div>
                <h2 className="font-satoshi mb-8 text-3xl font-bold">
                  Send Us a Message
                </h2>

                {submitted ? (
                  <p className="font-manrope text-lg text-gray-300">
                    Thanks for reaching out — we'll get back to you soon.
                  </p>
                ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="font-manrope mb-2 block text-sm font-medium text-gray-300"
                    >
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="font-manrope w-full rounded-lg border border-gray-700 bg-gray-900 px-4 py-3 text-gray-50 placeholder-gray-500 transition-all focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="font-manrope mb-2 block text-sm font-medium text-gray-300"
                    >
                      Work Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="font-manrope w-full rounded-lg border border-gray-700 bg-gray-900 px-4 py-3 text-gray-50 placeholder-gray-500 transition-all focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="company"
                        className="font-manrope mb-2 block text-sm font-medium text-gray-300"
                      >
                        Company Name *
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        required
                        value={formData.company}
                        onChange={handleChange}
                        className="font-manrope w-full rounded-lg border border-gray-700 bg-gray-900 px-4 py-3 text-gray-50 placeholder-gray-500 transition-all focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                        placeholder="Acme Inc."
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="phone"
                        className="font-manrope mb-2 block text-sm font-medium text-gray-300"
                      >
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="font-manrope w-full rounded-lg border border-gray-700 bg-gray-900 px-4 py-3 text-gray-50 placeholder-gray-500 transition-all focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="inquiryType"
                      className="font-manrope mb-2 block text-sm font-medium text-gray-300"
                    >
                      Inquiry Type *
                    </label>
                    <select
                      id="inquiryType"
                      name="inquiryType"
                      required
                      value={formData.inquiryType}
                      onChange={handleChange}
                      className="font-manrope w-full rounded-lg border border-gray-700 bg-gray-900 px-4 py-3 text-gray-50 transition-all focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                    >
                      <option value="general">General Inquiry</option>
                      <option value="sales">Sales & Demos</option>
                      <option value="support">Customer Support</option>
                      <option value="partnership">
                        Partnership Opportunities
                      </option>
                      <option value="careers">Careers</option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="font-manrope mb-2 block text-sm font-medium text-gray-300"
                    >
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      className="font-manrope w-full rounded-lg border border-gray-700 bg-gray-900 px-4 py-3 text-gray-50 placeholder-gray-500 transition-all focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                      placeholder="Tell us about your hiring needs..."
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-full"
                    disabled={submitting}
                  >
                    {submitting ? "Sending..." : "Send Message"}
                  </Button>

                  <p className="font-manrope text-sm text-gray-500">
                    By submitting this form, you agree to our{" "}
                    <a
                      href="/privacy"
                      className="text-teal-400 hover:underline"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </form>
                )}
              </div>

              {/* Contact Information */}
              <div className="space-y-8">
                <div>
                  <h2 className="font-satoshi mb-8 text-3xl font-bold">
                    Other Ways to Reach Us
                  </h2>

                  <div className="space-y-6">
                    {/* Sales */}
                    <div className="rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900 to-gray-950 p-6">
                      <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-teal-500/30 bg-teal-500/10">
                        <svg
                          className="h-6 w-6 text-teal-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                      </div>
                      <h3 className="font-satoshi mb-2 text-xl font-bold">
                        Sales Inquiries
                      </h3>
                      <p className="font-manrope mb-3 text-gray-400">
                        Interested in a demo or pricing information?
                      </p>
                      <a
                        href="mailto:sales@babblebots.ai"
                        className="font-manrope text-teal-400 hover:underline"
                      >
                        sales@babblebots.ai
                      </a>
                    </div>

                    {/* Support */}
                    <div className="rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900 to-gray-950 p-6">
                      <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10">
                        <svg
                          className="h-6 w-6 text-cyan-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                          />
                        </svg>
                      </div>
                      <h3 className="font-satoshi mb-2 text-xl font-bold">
                        Customer Support
                      </h3>
                      <p className="font-manrope mb-3 text-gray-400">
                        Need help with your account or have technical questions?
                      </p>
                      <a
                        href="mailto:support@babblebots.ai"
                        className="font-manrope text-cyan-400 hover:underline"
                      >
                        support@babblebots.ai
                      </a>
                    </div>

                    {/* Partnerships */}
                    <div className="rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900 to-gray-950 p-6">
                      <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-purple-500/30 bg-purple-500/10">
                        <svg
                          className="h-6 w-6 text-purple-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                      </div>
                      <h3 className="font-satoshi mb-2 text-xl font-bold">
                        Partnerships
                      </h3>
                      <p className="font-manrope mb-3 text-gray-400">
                        Interested in partnering with Babblebots?
                      </p>
                      <a
                        href="mailto:partnerships@babblebots.ai"
                        className="font-manrope text-purple-400 hover:underline"
                      >
                        partnerships@babblebots.ai
                      </a>
                    </div>
                  </div>
                </div>

                {/* Office Location */}
                <div className="rounded-2xl border border-gray-800 bg-gradient-to-br from-teal-900/10 via-gray-900 to-cyan-900/10 p-8">
                  <h3 className="font-satoshi mb-4 text-xl font-bold">
                    Our Office
                  </h3>
                  <p className="font-manrope mb-2 text-gray-300">
                    SINE Office, YP Rd, IIT Area, Powai
                  </p>
                  <p className="font-manrope text-gray-400">
                    Mumbai, Maharashtra 400076
                  </p>
                </div>

                {/* Social Media */}
                <div>
                  <h3 className="font-satoshi mb-4 text-xl font-bold">
                    Follow Us
                  </h3>
                  <div className="flex gap-4">
                    <a
                      href="https://linkedin.com/company/babblebots"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-12 w-12 items-center justify-center rounded-xl border border-gray-800 bg-gray-900 transition-all hover:border-teal-500 hover:bg-teal-500/10"
                    >
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    </a>

                    <a
                      href="https://twitter.com/babblebots"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-12 w-12 items-center justify-center rounded-xl border border-gray-800 bg-gray-900 transition-all hover:border-teal-500 hover:bg-teal-500/10"
                    >
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                    </a>

                    <a
                      href="https://youtube.com/@babblebots"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-12 w-12 items-center justify-center rounded-xl border border-gray-800 bg-gray-900 transition-all hover:border-teal-500 hover:bg-teal-500/10"
                    >
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;
